import { lazy } from 'react';
import { TenantRouteMeta } from '../types';

// R7: RBAC and SecretDefaultsAudit moved to /admin/*. Tenant-side page
// files under src/pages/RBAC/ and src/pages/SecretDefaults/ have been
// deleted and their tenant routes removed below.
// WU12 — Replaced the legacy single-page Environment Promotion with the
// workflow-centric PromotionPipeline. The detail-page child route
// (:workflowUuid) opens PromotionWorkflowDetail. URL slug stays the same so
// links from sidebars / bookmarks don't break.
const PromotionPipeline = lazy(
  () => import('../../pages/EnvironmentPromotion/PromotionPipeline'),
);
const PromotionWorkflowDetail = lazy(
  () => import('../../pages/EnvironmentPromotion/PromotionWorkflowDetail'),
);
const ApiKeyManagement = lazy(() => import('../../pages/ApiKeys/ApiKeyManagement'));
const BulkOperations = lazy(() => import('../../pages/BulkOperations/BulkOperations'));
const FileValidation = lazy(() => import('../../pages/FileValidation/FileValidation'));
const FinancialReconciliation = lazy(
  () => import('../../pages/FinancialReconciliation/FinancialReconciliation'),
);

/**
 * Administration + Financial surfaces. All require elevated roles. The
 * `role` metadata flows into both the RequireRole wrapper (when wired)
 * and the sidebar visibility filter.
 */
export const adminRoutes: TenantRouteMeta[] = [
  // Note: Users route is declared under observability (sidebar group "consumer-360")
  // because the historical sidebar treats it as Consumer 360. Avoid duplicating
  // the lazy import to prevent two chunks for the same module.

  // R7: `rbac` moved to /admin/rbac — tenant route retired.
  {
    path: 'api-keys',
    element: <ApiKeyManagement />,
    role: 'admin',
    permission: 'administration',
    sidebar: { group: 'administration', label: 'API Keys', order: 2 },
  },
  {
    path: 'environment-promotion',
    // Gated by RequireRole('promoter') / RequirePermission('promote') via
    // the metadata below — the sidebar hides the nav entry from
    // non-promoters/ungranted users, and this closes the direct-URL gap.
    element: <PromotionPipeline />,
    role: 'promoter',
    permission: 'promote',
    sidebar: { group: 'environment-promotion', label: 'Promotion Pipeline', order: 1 },
  },
  {
    // WU12 — workflow detail child. No sidebar entry; reached via
    // "Details ↗" on a workflow card or by direct URL.
    path: 'environment-promotion/:workflowUuid',
    element: <PromotionWorkflowDetail />,
    role: 'promoter',
    permission: 'promote',
  },
  {
    path: 'bulk-operations',
    element: <BulkOperations />,
    role: 'admin',
    permission: 'administration',
    sidebar: { group: 'administration', label: 'Bulk Operations', order: 4 },
  },
  {
    path: 'file-validation',
    element: <FileValidation />,
    role: 'admin',
    permission: 'administration',
    sidebar: { group: 'administration', label: 'File Validation', order: 5 },
  },
  // R7: `secret-defaults-audit` moved to /admin/secret-defaults-audit — tenant route retired.

  // Financial group
  {
    path: 'financial-reconciliation',
    element: <FinancialReconciliation />,
    role: 'admin',
    permission: 'financial',
    sidebar: { group: 'financial', label: 'Reconciliation' },
  },
];
